import { useMutation } from "@apollo/client";
import Print from "@mui/icons-material/Print";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CONTAINER_LABEL_EXPORT_CAP } from "encamp-shared/src/waste/labelExport";
import { LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useCallback, useState } from "react";
import { downloadFromUrl } from "util/download";

import { EXPORT_CONTAINER_LABELS_MUTATION } from "../api";
import { FileTypeToggle, FormatRadioGroup } from "./LabelFormatControls";
import { Eyebrow } from "./LabelScreenTypography";

const FILE_EXTENSION: Record<LabelExportFormat, string> = {
  [LabelExportFormat.Pdf]: "pdf",
  [LabelExportFormat.Png]: "png",
};

interface BulkExportLabelsDialogProps {
  open: boolean;
  /** Snapshotted when the dialog opens so the mutation payload and the
   * PNG-disable check can't shift out from under the user mid-flow. */
  containerIds: string[];
  onClose: () => void;
  /** Called once the export mutation has succeeded and the file has
   * downloaded -- the caller closes the dialog. */
  onSuccess: () => void;
  /**
   * Called unconditionally once the confirm attempt settles, success or
   * error -- the dialog's payload is a snapshot taken when it opened, not
   * re-read from the grid, so clearing the live selection here can't affect
   * a retry. Mirrors the NotificationBulkActions.tsx try/finally shape.
   */
  clearSelection: () => void;
}

/** Confirm dialog for the container list's bulk print/export action.
 * Exported separately from the trigger button so tests can drive it with an
 * explicit `containerIds` snapshot instead of a mounted grid selection. */
export function BulkExportLabelsDialog({
  open,
  containerIds,
  onClose,
  onSuccess,
  clearSelection,
}: BulkExportLabelsDialogProps) {
  const [layout, setLayout] = useState<LabelFormat>(LabelFormat.Full);
  const [fileType, setFileType] = useState<LabelExportFormat>(LabelExportFormat.Pdf);
  // No UI sets this anymore -- see the "Not showing the Encamp-logo switch"
  // comment below -- but it stays real state (not a literal `true`) so
  // re-enabling the switch is just adding back a control wired to it.
  const [branded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportLabels, { loading }] = useMutation(EXPORT_CONTAINER_LABELS_MUTATION);

  // The backend rejects a PNG export for more than one container -- disable
  // it here so users never hit that server error.
  const pngDisabled = containerIds.length > 1;

  // The backend also caps how many containers one export may cover -- same
  // idea as pngDisabled: block Confirm up front instead of surfacing the
  // server's rejection through the error alert after a round trip.
  const overCap = containerIds.length > CONTAINER_LABEL_EXPORT_CAP;

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      const { data } = await exportLabels({
        variables: { containerIds, layout, fileType, branded },
      });
      const url = data?.exportContainerLabels.url;
      if (url) {
        await downloadFromUrl(url, `container-labels.${FILE_EXTENSION[fileType]}`);
      }
      onSuccess();
    } catch (mutationError) {
      setError(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to export labels. Try again."
      );
    } finally {
      clearSelection();
    }
  }, [exportLabels, containerIds, layout, fileType, branded, onSuccess, clearSelection]);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      // Match the label screen's Print & format column width -- these are
      // the same controls, and at the ConfirmDialog default (440-640px) they
      // stretch wider than the surface they're copied from.
      minWidth="400px"
      maxWidth="400px"
      header="Export labels"
      confirmText="Export"
      loading={loading}
      disableConfirm={overCap}
      onConfirm={handleConfirm}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {containerIds.length} container{containerIds.length === 1 ? "" : "s"} selected.
        </Typography>
        {overCap && (
          <Typography variant="caption" color="text.secondary">
            Select {CONTAINER_LABEL_EXPORT_CAP} or fewer containers to export at once.
          </Typography>
        )}

        <Eyebrow mt={0}>Print format</Eyebrow>
        <FormatRadioGroup layout={layout} onChange={setLayout} />

        <Eyebrow>File type</Eyebrow>
        <FileTypeToggle value={fileType} onChange={setFileType} disablePng={pngDisabled} />
        {pngDisabled && (
          <Typography variant="caption" color="text.secondary">
            PNG only supports a single container at a time. Choose PDF to export multiple labels, or
            select just one container for a PNG.
          </Typography>
        )}

        {/* Not showing the Encamp-logo switch for now -- re-enable if anyone
            asks to hide the Encamp logo in the QR. `branded` still defaults
            true and is still sent on export. */}

        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </ConfirmDialog>
  );
}

/** Command-bar button wired to the grid selection -- shows the selected
 * count, snapshots the ids on open, and closes the dialog once the export
 * succeeds. The grid selection itself clears whenever a confirm attempt
 * settles (success or error), inside the dialog. */
export function BulkExportLabelsButton() {
  const { selectedCount, getSelectedIds, clearSelection } = useGridSelection();
  const [open, setOpen] = useState(false);
  const [containerIds, setContainerIds] = useState<string[]>([]);

  const handleOpen = useCallback(() => {
    setContainerIds(getSelectedIds().map(String));
    setOpen(true);
  }, [getSelectedIds]);

  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Print />}
        onClick={handleOpen}
        disabled={selectedCount === 0}
      >
        Export labels{selectedCount > 0 ? ` (${selectedCount})` : ""}
      </Button>
      {open && (
        <BulkExportLabelsDialog
          open
          containerIds={containerIds}
          onClose={handleClose}
          onSuccess={handleClose}
          clearSelection={clearSelection}
        />
      )}
    </>
  );
}
